import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, Divider, Stack, Toolbar, Typography } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { useApi } from '@/providers/ApiContext';
import { deleteOrganization } from '@/actions/org';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import OrganizationsGrid from './OrganizationsGrid';
import OrganizationDeleteDialog from './OrganizationDeleteDialog';

function Organizations(): React.JSX.Element {
  const navigate = useNavigate();
  const api = useApi();

  const [selected_ids, setSelectedIds] = useState<string[]>([]);
  const [delete_dialog_open, setDeleteDialogOpen] = useState<boolean>(false);
  const [refresh_key, setRefreshKey] = useState<number>(0);
  const [api_error, setApiError] = useState<string | null>(null);

  const has_single_selection = useMemo(() => {
    return selected_ids.length === 1;
  }, [selected_ids.length]);

  const handleDeleteOrganizations = useCallback(
    async (organization_ids: string[]): Promise<void> => {
      if (!api || organization_ids.length !== 1) return;

      try {
        await deleteOrganization(api, organization_ids[0]);
        setRefreshKey((current_refresh_key) => current_refresh_key + 1);
        setSelectedIds([]);
      } catch (err) {
        const error = err as Error;
        setApiError(
          error.message ||
            'Unable to delete organization. This could be due to network issues or server problems.',
        );
      }
    },
    [api],
  );

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Toolbar sx={{ minHeight: '55px !important' }}>
          <Typography variant='h6' sx={{ mr: 4 }}>
            Organizations
          </Typography>

          <Stack direction='row' spacing={1} alignItems='center'>
            <Button
              sx={{ padding: '3px 9px' }}
              variant='outlined'
              onClick={() => navigate('/organizations/edit')}
              color='primary'
            >
              <Stack direction='row' spacing={1} alignItems='center'>
                <AddCircleOutlineOutlinedIcon color='primary' />
                <Typography color='primary' variant='button' display='block'>
                  Create
                </Typography>
              </Stack>
            </Button>

            <Button
              sx={{ padding: '3px 9px' }}
              onClick={() => {
                if (has_single_selection) {
                  navigate(`/organizations/edit?organization_id=${selected_ids[0]}`);
                }
              }}
              disabled={!has_single_selection}
            >
              <Stack direction='row' spacing={1} alignItems='center'>
                <EditOutlinedIcon />
                <Typography variant='button' display='block'>
                  Edit
                </Typography>
              </Stack>
            </Button>

            <Button
              sx={{ padding: '3px 9px' }}
              onClick={() => {
                if (has_single_selection) {
                  setDeleteDialogOpen(true);
                }
              }}
              disabled={!has_single_selection}
            >
              <Stack direction='row' spacing={1} alignItems='center'>
                <DeleteOutlinedIcon />
                <Typography variant='button' display='block'>
                  Delete
                </Typography>
              </Stack>
            </Button>
          </Stack>
        </Toolbar>
        <Divider />
        <Box sx={{ p: 2 }}>
          <OrganizationsGrid
            selected_ids={selected_ids}
            onSelectionChange={setSelectedIds}
            refresh_key={refresh_key}
          />
        </Box>
      </Box>

      <OrganizationDeleteDialog
        open={delete_dialog_open}
        handleClose={() => setDeleteDialogOpen(false)}
        organization_ids={selected_ids}
        deleteOrganizations={handleDeleteOrganizations}
      />

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </>
  );
}

export default Organizations;

